<template>
  <div class="p-6 bg-white/60 rounded-xl min-h-screen">
    <div class="flex justify-between mb-6">
      <div>
        <h6 class="bg-clip-text text-transparent bg-gradient-to-r from-purple-500 via-pink-500 to-blue-500 hover:animate-pulse">
        داشبورد گزارش کمبود موجودی (به تفکیک تامین‌کننده)
        </h6>
      </div>
      <v-spacer></v-spacer>
      <div v-if="brandsWithLow.length > 0">
        <button
            @click="exportExcel"
            :disabled="!canExport"
            class="px-3 py-2 bg-green-500 ml-2 text-white rounded-lg shadow hover:shadow-md transition transform hover:scale-105 text-sm disabled:opacity-50"
        >
          خروجی اکسل
        </button>

        <button
            @click="printTable"
            :disabled="!canExport"
            class="px-3 py-2 bg-blue-500 text-white rounded-lg shadow hover:shadow-md transition transform hover:scale-105 text-sm disabled:opacity-50"
        >
          چاپ گزارش
        </button>
      </div>
    </div>
    <div class="bg-white bg-opacity-70 backdrop-filter backdrop-blur-lg rounded-xl p-3 mb-6 shadow-sm border border-gray-100">
      <div class="flex justify-between items-end gap-3">

        <!-- فیلدهای سمت راست -->
        <div class="flex gap-4">
          <div>
            <label class="block text-xs text-gray-600 mb-1">تامین‌کننده</label>
            <select v-model="selectedSupplier" class="px-2 py-2 bg-gray-100 text-sm rounded-lg border w-full text-right">
              <option value="">انتخاب تامین‌کننده</option>
              <option v-for="s in suppliers" :key="s.value" :value="s.value">
                {{ s.name }}
              </option>
            </select>
          </div>
          <div>
            <label class="block text-xs text-gray-600 mb-1">حداقل موجودی (عدد)</label>
            <input
                v-model.number="minimumInventory"
                type="number"
                min="0"
                class="px-2 py-2 bg-gray-100 text-sm rounded-lg border w-full text-right"
                placeholder="مثال: 5"
                @keyup.enter="fetchLowInventory"
            />
          </div>
        </div>

        <div class="flex items-end gap-2">
          <v-spacer></v-spacer>
          <button
              v-if="brandsWithLow.length > 0"
              @click="resetControls"
              class="px-3 py-2 bg-red-700 rounded-full border shadow-sm hover:bg-gray-50 text-sm"
          >
            <v-icon color="white">fa-times</v-icon>
          </button>
          <button
              @click="fetchLowInventory"
              :disabled="!canFetch"
              class="px-4 py-2 bg-gradient-to-r from-red-500 to-blue-600 text-white rounded-lg shadow hover:shadow-md transition transform hover:scale-105 disabled:opacity-50"
          >
            دریافت گزارش
          </button>
        </div>
      </div>
    </div>

    <!-- Result: brands -> products -->
    <div v-if="loading" class="p-6 bg-white bg-opacity-60 rounded-xl shadow-sm text-center">
      <svg class="animate-spin h-6 w-6 mx-auto text-blue-500" xmlns="http://www.w3.org/2000/svg" fill="none"
           viewBox="0 0 24 24">
        <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
        <path class="opacity-75" fill="currentColor"
              d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"></path>
      </svg>
      <div class="mt-2 text-gray-600">در حال بارگذاری...</div>
    </div>

    <div v-else>
      <div v-if="brandsWithLow.length === 0" class="p-6 bg-white bg-opacity-60 rounded-xl shadow-sm text-center text-gray-600">
        محصولی مطابق فیلتر یافت نشد.
      </div>

      <div v-else class="space-y-6">
        <div
            v-for="brand in brandsWithLow"
            :key="brand.brand_id"
            class="bg-white bg-opacity-70 backdrop-filter backdrop-blur-lg rounded-xl p-4 shadow-sm border border-gray-100"
        >
          <div class="flex items-center justify-between mb-4">
            <div class="flex items-center gap-3">
              <div v-if="brand.brand_logo" class="w-12 h-12 rounded-md overflow-hidden border">
                <img :src="brand.brand_logo" class="w-full h-full object-cover" />
              </div>
              <div>
                <div class="text-sm font-semibold text-gray-800">{{ brand.brand_name || 'بدون برند' }}</div>
                <div class="text-xs text-gray-500">تعداد محصول: {{ brand.products.length }}</div>
              </div>
            </div>
            <div class="text-xs text-gray-500">حداقل مورد: {{ minimumInventory }}</div>
          </div>

          <div class="overflow-x-auto">
            <table class="min-w-full text-sm">
              <thead class="bg-gray-50 bg-opacity-60">
              <tr>
                <th class="p-3 text-right text-xs text-gray-500">#</th>
                <th class="p-3 text-right text-xs text-gray-500">تصویر</th>
                <th class="p-3 text-right text-xs text-gray-500">نام محصول</th>
                <th class="p-3 text-right text-xs text-gray-500">کد ۱۶ رقمی</th>
                <th class="p-3 text-right text-xs text-gray-500">موجودی فعلی</th>
              </tr>
              </thead>
              <tbody>
              <tr v-for="(p, idx) in brand.products" :key="p.id" class="border-t border-gray-100">
                <td class="p-3 text-[11px] text-gray-800">{{ idx + 1 }}</td>
                <td class="p-3">
                  <div v-if="p.picture">
                    <img :src="img_source(p.picture)" class="w-12 h-12 rounded-md object-cover border" />
                  </div>
                  <div v-else class="w-12 h-12 rounded-md bg-gray-100 flex items-center justify-center text-xs text-gray-400">
                    NO
                  </div>
                </td>
                <td class="p-3 text-[12px] font-medium text-gray-900">{{ p.name }}</td>
                <td class="p-3 text-[11px] text-gray-600">{{ p.sixteen_digit_code || '-' }}</td>
                <td class="p-3 text-[12px]">
                    <span v-if="(p.current_inventory || p.current_inventory === 0) && p.current_inventory > 0" class="text-sm font-semibold">
                      {{ p.current_inventory }}
                    </span>
                  <span v-else class="bg-red-200 text-red-700 px-2 py-0.5 rounded-full text-xs">اتمام</span>
                </td>
              </tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>

  </div>
</template>

<script>
export default {
  name: "SupplierLowInventoryByBrand",
  data() {
    return {
      suppliers: [],
      selectedSupplier: '',
      minimumInventory: 10,
      loading: false,
      brandsWithLow: [],
      exporting: false,
    }
  },
  computed: {
    canFetch() {
      return !!this.selectedSupplier && (this.minimumInventory !== null && this.minimumInventory !== '' && !isNaN(this.minimumInventory))
    },
    canExport() {
      return !!this.selectedSupplier && this.canFetch && !this.loading && !this.exporting
    }
  },
  methods: {
    fetchSuppliers() {
      this.request({
        url: this.endpoint('main/supplier'),
        method: 'get',
        success: (data) => {
          this.suppliers = []
          for (let supplier of data) {
            this.suppliers.push({
              name: supplier.name,
              value: supplier.id
            })
          }
        },
        error: (err) => {
          console.error('Failed to fetch suppliers', err)
        }
      })
    },

    fetchLowInventory() {
      if (!this.canFetch) {
        alert('لطفا تامین‌کننده و حداقل موجودی را وارد کنید.')
        return
      }

      this.loading = true
      this.brandsWithLow = []

      const params = {
        supplier_id: this.selectedSupplier,
        minimum_inventory: this.minimumInventory
      }

      this.request({
        url: this.endpoint('product/supplierLowInventoryByBrand'),
        method: 'get',
        params,
        success: (data) => {
          // API expected: list of brands each with products
          // normalize keys (in case API uses slightly different names)
          this.brandsWithLow = (data || []).map(b => {
            return {
              brand_id: b.brand_id || b.id || null,
              brand_name: b.brand_name || b.name || null,
              brand_logo: b.brand_logo || (b.brand && b.brand.logo) || null,
              products: (b.products || []).map(p => ({
                id: p.id,
                name: p.name,
                picture: p.picture || p.image || null,
                sixteen_digit_code: p.sixteen_digit_code || p.sixteenDigitCode || null,
                current_inventory: p.current_inventory || p.calculate_current_inventory || p.currentInventory || 0
              }))
            }
          })

          this.loading = false
        },
        error: (err) => {
          this.loading = false
          console.error('fetch error', err)
          alert('خطا در دریافت اطلاعات. کنسول را بررسی کنید.')
        }
      })
    },

    resetControls() {
      this.selectedSupplier = ''
      this.minimumInventory = 1
      this.brandsWithLow = []
    },

    refresh() {
      if (this.brandsWithLow.length > 0) {
        this.fetchLowInventory()
      } else {
        this.fetchSuppliers()
      }
    },
    downloadExport(format) {
      if (!this.canExport) return

      this.exporting = true

      const base = this.endpoint(`product/export/supplier-low-inventory-by-brand/${format}`)
      const url = `${base}?token=${this.token}&supplier_id=${encodeURIComponent(this.selectedSupplier)}&minimum_inventory=${encodeURIComponent(this.minimumInventory)}`

      const newWindow = window.open(url, '_blank')
      if (!newWindow) window.location.href = url

      setTimeout(() => {
        this.exporting = false
      }, 2000)
    },

    exportExcel() {
      // ✅ مثل نمونه‌ی قبلی
      this.downloadExport('xlsx')
    },

    printTable() {
      // ✅ مثل نمونه‌ی قبلی
      this.downloadExport('print')
    },
    img_source(src) {
      if (!src) return ''
      try {
        if (src.startsWith('http://') || src.startsWith('https://')) return src
      } catch (e) {}
      return src
    }
  },
  mounted() {
    if (this.$route && this.$route.query && this.$route.query.supplier) {
      this.selectedSupplier = this.$route.query.supplier
    }
    this.fetchSuppliers()
    if (this.$route && this.$route.query) {
      if (this.$route.query.supplier) this.selectedSupplier = this.$route.query.supplier
      if (this.$route.query.min_inventory) this.minimumInventory = Number(this.$route.query.min_inventory)
      if (this.selectedSupplier && this.minimumInventory !== null) {
        this.fetchLowInventory()
      }
    }
  }
}
</script>

<style scoped>
/* RTL support */
:root {
  direction: rtl;
}

/* Smooth transitions */
.transition-all {
  transition-property: all;
  transition-timing-function: cubic-bezier(0.4, 0, 0.2, 1);
  transition-duration: 150ms;
}
</style>
